import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { AlertCircle, Loader2, CheckCircle, Mail, Eye, EyeOff, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/utils/trpc";

function getQueryParams(): { email: string; code: string } {
  if (typeof window === "undefined") return { email: "", code: "" };
  const params = new URLSearchParams(window.location.search);
  return {
    email: params.get("email") || "",
    code: params.get("code") || "",
  };
}

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [hasCodeFromUrl, setHasCodeFromUrl] = useState(false);
  const [sent, setSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    const { email: urlEmail, code: urlCode } = getQueryParams();
    setEmail(urlEmail);
    if (urlCode) {
      setCode(urlCode);
      setHasCodeFromUrl(true);
    }
  }, []);

  const requestReset = trpc.auth.requestPasswordReset.useMutation({
    onSuccess: () => {
      setSent(true);
      setError(null);
      setInfo("If an account exists for this email, a reset code has been sent.");
    },
    onError: (err: any) => {
      setError(err?.message || "Failed to send reset code.");
      setInfo(null);
    },
  });

  const resetPassword = trpc.auth.resetPassword.useMutation({
    onSuccess: (data) => {
      if (data.sessionToken) {
        localStorage.setItem("app_session_token", data.sessionToken);
      }
      window.location.href = "/";
    },
    onError: (err: any) => {
      const code = typeof err?.message === "string" ? err.message : "";
      if (code === "INVALID_OR_EXPIRED_RESET_CODE") {
        setError("Invalid or expired reset code.");
        return;
      }
      if (code === "USER_NOT_FOUND") {
        setError("No account found with that email.");
        return;
      }

      // Handle Zod validation errors - try multiple error formats
      if (err?.data?.zodError) {
        const zodError = err.data.zodError;
        // Try fieldErrors format
        if (zodError.fieldErrors?.newPassword?.[0]) {
          setError(zodError.fieldErrors.newPassword[0]);
          return;
        }
      }

      // Handle raw message that might contain validation error
      if (err?.message) {
        try {
          const parsed = JSON.parse(err.message);
          if (Array.isArray(parsed) && parsed[0]?.message) {
            setError(parsed[0].message);
            return;
          }
          if (parsed.message) {
            setError(parsed.message);
            return;
          }
        } catch {
          // Not JSON, use as-is
          if (err.message && err.message !== "[object Object]") {
            setError(err.message);
            return;
          }
        }
      }

      setError("Failed to reset password. Please check your password meets the requirements.");
    },
  });

  const isBusy = requestReset.isPending || resetPassword.isPending;

  const canSend = useMemo(() => email.includes("@"), [email]);
  const canReset = useMemo(() => {
    if (!email || !code || !newPassword || !confirmPassword) return false;
    if (newPassword !== confirmPassword) return false;
    return true;
  }, [email, code, newPassword, confirmPassword]);

  // If code is in URL (from email link), show simplified password-only form
  if (hasCodeFromUrl) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F5F5F5]">
        <div className="w-full max-w-md px-8">
          <div className="bg-white rounded-2xl shadow-lg p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#5B2C93]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-[#5B2C93]" />
              </div>
              <h1 className="text-2xl font-medium text-[#2C2C2C] mb-2">
                Create New Password
              </h1>
              <p className="text-[#6B6B6B] text-sm">
                Enter a new password for <span className="font-medium text-[#2C2C2C]">{email}</span>
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {info && (
              <Alert className="mb-6 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">{info}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2C2C]">
                  New password
                </label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className="h-12 pl-4 pr-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute top-1/2 right-4 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B6B6B]"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2C2C]">
                  Confirm password
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 pl-4 pr-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute top-1/2 right-4 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B6B6B]"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-sm text-red-600">Passwords do not match.</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) =>
                    setRememberMe(checked as boolean)
                  }
                />
                <label
                  htmlFor="remember"
                  className="text-sm text-[#6B6B6B] cursor-pointer"
                >
                  Remember this device for 30 days
                </label>
              </div>

              <Button
                type="button"
                disabled={isBusy || !canReset}
                onClick={() => {
                  setError(null);
                  setInfo(null);
                  resetPassword.mutate({
                    email,
                    code,
                    newPassword,
                    rememberMe,
                  });
                }}
                className="w-full h-12 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white font-medium rounded-lg text-base"
              >
                {resetPassword.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  "Reset password and sign in"
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setLocation("/login")}
                  className="text-sm text-[#5B2C93] hover:underline"
                >
                  Back to login
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <span className="text-sm text-[#6B6B6B]">© 2026 Permit3</span>
          </div>
        </div>
      </div>
    );
  }

  // Default view: Full reset password form with email input and code entry
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F5F5F5]">
      <div className="w-full max-w-md px-8">
        <div className="bg-white rounded-2xl shadow-lg p-10">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-medium text-[#2C2C2C] mb-2">
              Reset Password
            </h1>
            <p className="text-[#6B6B6B] text-sm">
              Enter your email to receive a reset code.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {info && (
            <Alert className="mb-6">
              <AlertDescription>{info}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2C2C2C]">Email</label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-4 pr-4 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
              />
            </div>

            <Button
              type="button"
              disabled={isBusy || !canSend || sent}
              onClick={() => {
                setError(null);
                setInfo(null);
                requestReset.mutate({ email });
              }}
              className="w-full h-12 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white font-medium rounded-lg text-base"
            >
              {requestReset.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : sent ? (
                "Code sent!"
              ) : (
                "Send reset code"
              )}
            </Button>

            <div className="border-t pt-5">
              <p className="text-sm text-[#6B6B6B] mb-4">
                Already have a code? Enter it and choose a new password.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#2C2C2C]">
                    Reset code
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.trim())}
                    className="h-12 pl-4 pr-4 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#2C2C2C]">
                    New password
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter a strong password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="h-12 pl-4 pr-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B6B6B]"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#2C2C2C]">
                    Confirm password
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="h-12 pl-4 pr-12 bg-[#F5F5F5] border-[#E0E0E0] rounded-lg focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B6B6B]"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-sm text-red-600">Passwords do not match.</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) =>
                      setRememberMe(checked as boolean)
                    }
                  />
                  <label
                    htmlFor="remember"
                    className="text-sm text-[#6B6B6B] cursor-pointer"
                  >
                    Remember this device for 30 days
                  </label>
                </div>

                <Button
                  type="button"
                  disabled={isBusy || !canReset}
                  onClick={() => {
                    setError(null);
                    setInfo(null);
                    resetPassword.mutate({
                      email,
                      code,
                      newPassword,
                      rememberMe,
                    });
                  }}
                  className="w-full h-12 bg-[#111827] hover:bg-[#0b1220] text-white font-medium rounded-lg text-base"
                >
                  {resetPassword.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    "Reset password and sign in"
                  )}
                </Button>
              </div>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setLocation("/login")}
                className="text-sm text-[#5B2C93] hover:underline"
              >
                Back to login
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <span className="text-sm text-[#6B6B6B]">© 2026 Permit3</span>
        </div>
      </div>
    </div>
  );
}

